import { normalizeTargetingAnswers } from '@/helpers/streamsTargeting';
import localStorageRepositories from '@/plugins/localStorageRepositories';
import { resolveClosedCampaignThumbnail } from '@/helpers/closedCampaignThumbnail';
import { getCampaignCircleItemWidth, getCampaignReadStatusBorderWidth } from '@/helpers/campaignSettings';
import { isReelLike } from '@/helpers/isReelLike';
import {
  applyFeedFolderSaleLabel,
  normalizeFeedFolderSaleLabelColor,
  FEED_FOLDER_SALE_LABEL_DEFAULT_TEXT_COLOR,
  FEED_FOLDER_SALE_LABEL_DEFAULT_BACKGROUND_COLOR,
} from '@/helpers/feedFolderSaleLabel';

const DEFAULT_CIRCLE_THUMBNAIL_SIZE = 80;
const DEFAULT_SQUARE_THUMBNAIL_WIDTH = 182;
const DEFAULT_SQUARE_THUMBNAIL_HEIGHT = 324;

export const rememberPreparedCampaignForDisplay = (campaign, preparedCampaign, answers = {}) => {
  if (!campaign || !preparedCampaign) return;

  // 初期描画時とオープン後で同じ表示内容を再利用できるよう、描画結果を保持する
  campaign.__storeeelPreparedCampaignForDisplay = preparedCampaign;
  campaign.__storeeelPreparedCampaignForDisplayAnswers = JSON.stringify(normalizeTargetingAnswers(answers));
};

const buildCampaignStyle = (campaign) => {
  const unseenColor = campaign?.unreadColorCode || '#000000';
  const seenColor = campaign?.readColorCode || '#A3A3A3';
  const groupNameColor = campaign?.groupNameColorCode || campaign?.group_name_color_code || null;
  const groupNameFontFamily = campaign?.groupNameFontFamily || campaign?.group_name_font_family || null;
  const groupNameFontWeight = campaign?.groupNameFontWeight || campaign?.group_name_font_weight || null;
  const groupNameFontSize = campaign?.groupNameFontSize || campaign?.group_name_font_size || null;
  const thumbnailScale = campaign?.thumbnailScale || campaign?.thumbnail_scale || null;
  const readStatusBorderWidth = getCampaignReadStatusBorderWidth(campaign);
  const circleIconBorder = campaign?.isReadStatusVisible === true ? 'none' : '1px solid #e0e0e0';

  const styleVars = [
    `--storeeel-unseen-border-color: ${unseenColor}`,
    `--storeeel-seen-border-color: ${seenColor}`,
    `--storeeel-read-status-ring-size: ${readStatusBorderWidth}px`,
  ];
  if (groupNameColor) {
    styleVars.push(`--storeeel-group-name-color: ${groupNameColor}`);
  }
  if (groupNameFontFamily) {
    styleVars.push(`--storeeel-group-name-font-family: ${groupNameFontFamily}`);
  }
  if (groupNameFontWeight) {
    styleVars.push(`--storeeel-group-name-font-weight: ${groupNameFontWeight}`);
  }
  if (groupNameFontSize) {
    styleVars.push(`--storeeel-group-name-font-size: ${Number(groupNameFontSize)}px`);
  }
  if (campaign?.frame === 'circle' && thumbnailScale) {
    const circleThumbnailSize = Math.round(DEFAULT_CIRCLE_THUMBNAIL_SIZE * Number(thumbnailScale));
    styleVars.push(`--storeeel-circle-thumbnail-size: ${circleThumbnailSize}px`);
    styleVars.push(`--storeeel-group-name-width: ${Math.max(circleThumbnailSize, 92)}px`);
    styleVars.push(`--storeeel-circle-item-width: ${getCampaignCircleItemWidth(campaign)}`);
    styleVars.push(`--storeeel-circle-icon-border: ${circleIconBorder}`);
  } else if (campaign?.frame === 'circle') {
    styleVars.push(`--storeeel-circle-item-width: ${getCampaignCircleItemWidth(campaign)}`);
    styleVars.push(`--storeeel-circle-icon-border: ${circleIconBorder}`);
  }
  // リール型・タイル型はサムネ寸法CSS変数を設定する（タイルはビューア内で使用）
  if (isReelLike(campaign?.frame) && thumbnailScale) {
    const squareThumbnailWidth = Math.round(DEFAULT_SQUARE_THUMBNAIL_WIDTH * Number(thumbnailScale));
    const squareThumbnailHeight = Math.round(DEFAULT_SQUARE_THUMBNAIL_HEIGHT * Number(thumbnailScale));
    styleVars.push(`--storeeel-square-thumbnail-width: ${squareThumbnailWidth}px`);
    styleVars.push(`--storeeel-square-thumbnail-height: ${squareThumbnailHeight}px`);
    styleVars.push(`--storeeel-group-name-width: ${Math.max(squareThumbnailWidth, 92)}px`);
  }
  return `${styleVars.join('; ')};`;
};

const appendDeferredVideoNode = (container, thumbnail, contentGroup, content, className, videoStyle = '') => {
  if (!thumbnail?.playbackVideoUrl) return;

  const video = document.createElement('video');
  video.className = `${className} storeeel-content-movie`;
  video.setAttribute('data-storeeel-preview-video', 'true');
  video.setAttribute('data-storeeel-content-id', content?.id || '');
  video.setAttribute('preload', 'none');
  video.setAttribute('playsinline', '');
  video.setAttribute('muted', '');
  video.setAttribute('loop', '');
  video.setAttribute('aria-label', contentGroup?.name || '');
  video.muted = true;
  video.style.display = 'none';
  if (videoStyle) {
    video.style.cssText += videoStyle;
  }
  container.appendChild(video);
};

const updateSquareThumbnailNode = (iconRoot, campaign, contentGroup) => {
  const content = contentGroup?.contents?.[0];
  const thumbnail = resolveClosedCampaignThumbnail(campaign, contentGroup, content);
  if (!iconRoot || !content || !thumbnail?.url) return false;

  iconRoot.className = `storeeel_campaign storeeel-icon-square campaign_${campaign.id}`;
  iconRoot.id = `storeeel-icon-${contentGroup.id}`;
  iconRoot.setAttribute('data-type', contentGroup?.name || '');
  iconRoot.setAttribute('group-name', contentGroup?.name || '');
  iconRoot.setAttribute('style', buildCampaignStyle(campaign));
  iconRoot.replaceChildren();

  const wrapper = document.createElement('div');
  wrapper.className = 'storeeel-square-content-wrapper';
  iconRoot.appendChild(wrapper);

  wrapper.replaceChildren();
  if (thumbnail.playbackVideoUrl) {
    wrapper.dataset.storeeelVideoSrc = thumbnail.playbackVideoUrl;
  } else {
    delete wrapper.dataset.storeeelVideoSrc;
  }

  if (thumbnail.kind === 'image') {
    const image = document.createElement('div');
    image.className = 'storeeel-content storeeel-content-image';
    image.id = `storeeel-content-${content.id}`;
    image.setAttribute('data-storeeel-preview-image', 'true');
    image.setAttribute('role', 'img');
    image.setAttribute('aria-label', contentGroup?.name || '');
    image.style.backgroundImage = `url('${thumbnail.url}')`;
    image.style.backgroundSize = 'cover';
    image.style.backgroundPosition = 'center';
    image.style.backgroundRepeat = 'no-repeat';
    wrapper.appendChild(image);
    appendDeferredVideoNode(wrapper, thumbnail, contentGroup, content, 'storeeel-content');
  } else if (thumbnail.kind === 'video') {
    const video = document.createElement('video');
    video.className = 'storeeel-content storeeel-content-movie';
    video.id = `storeeel-content-${content.id}`;
    video.src = `${thumbnail.url}#t=0.1`;
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.muted = true;
    wrapper.appendChild(video);
  }

  if (content?.mediaAsset?.type === 'adMediaAsset') {
    const badge = document.createElement('div');
    badge.className = 'storeeel-square-ad-badge';
    badge.innerHTML = '<div class="storeeel-square-ad-badge__text">PR</div>';
    wrapper.appendChild(badge);
  }

  // 連携配信のセール商品ラベル。初回描画（icons.js）と同じ見た目になるよう、
  // DOMを組み直すこの経路でも同じクラス名で付ける（CSSはicons.jsが注入する）。
  // 中身も icons.js と揃えて表示形式（saleLabel）に従う
  if (content?.showSaleLabel) {
    const saleBadge = document.createElement('div');
    saleBadge.className = 'storeeel-square-sale-badge';
    const saleBadgeText = document.createElement('div');
    saleBadgeText.className = 'storeeel-square-sale-badge__text';
    // 配色も icons.js と同じくインラインで当てる。クラスだけだとこの経路で描き直したときに
    // 既定色へ戻ってしまい、同じキャンペーンなのに描画経路で色が変わる
    saleBadge.style.background = normalizeFeedFolderSaleLabelColor(
      content.saleLabelBackgroundColor,
      FEED_FOLDER_SALE_LABEL_DEFAULT_BACKGROUND_COLOR,
    );
    saleBadgeText.style.color = normalizeFeedFolderSaleLabelColor(
      content.saleLabelTextColor,
      FEED_FOLDER_SALE_LABEL_DEFAULT_TEXT_COLOR,
    );
    applyFeedFolderSaleLabel(saleBadgeText, content.saleLabel);
    saleBadge.appendChild(saleBadgeText);
    wrapper.appendChild(saleBadge);
  }

  const groupName = document.createElement('div');
  groupName.className = 'storeeel_campaign_group_name';
  groupName.textContent = contentGroup?.name || '';
  iconRoot.appendChild(groupName);

  return true;
};

const createCircleMediaNode = (contentGroup, content) => {
  const thumbnail = resolveClosedCampaignThumbnail({ frame: 'circle' }, contentGroup, content);
  if (!thumbnail?.url) return null;

  if (thumbnail.kind === 'image') {
    const image = document.createElement('div');
    image.className = 'storeeel-icon storeeel-content-image';
    image.id = `storeeel-content-${content?.id || contentGroup?.id || ''}`;
    image.setAttribute('data-storeeel-preview-image', 'true');
    image.setAttribute('role', 'img');
    image.setAttribute('aria-label', contentGroup?.name || '');
    image.style.backgroundImage = `url('${thumbnail.url}')`;
    image.style.backgroundSize = 'cover';
    image.style.backgroundPosition = 'center';
    image.style.backgroundRepeat = 'no-repeat';
    image.style.display = 'block';
    image.style.padding = '0';
    return image;
  }

  if (thumbnail.kind === 'video') {
    const video = document.createElement('video');
    video.className = 'storeeel-icon storeeel-content-movie';
    video.id = `storeeel-content-${content.id}`;
    video.src = `${thumbnail.url}#t=0.1`;
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.muted = true;
    video.style.borderRadius = '50%';
    video.style.objectFit = 'cover';
    video.style.display = 'block';
    return video;
  }

  return null;
};

const updateCircleThumbnailNode = (iconRoot, campaign, contentGroup) => {
  const content = contentGroup?.contents?.[0];
  const thumbnail = resolveClosedCampaignThumbnail({ frame: 'circle' }, contentGroup, content);
  const mediaNode = createCircleMediaNode(contentGroup, content);
  if (!iconRoot || !content || !mediaNode || !thumbnail) return false;

  const showReadStatus = campaign?.isReadStatusVisible === true;
  const allSeen = (contentGroup?.contents || []).length > 0
    && contentGroup.contents.every((item) => localStorageRepositories.get('contentSeen')?.has?.(item.id));
  const isAdMediaAsset = content?.mediaAsset?.type === 'adMediaAsset';

  iconRoot.className = `storeeel_campaign storeeel-icon campaign_${campaign.id} storeeel_campaign_circle`;
  iconRoot.id = `storeeel-icon-${contentGroup.id}`;
  iconRoot.setAttribute('data-type', contentGroup?.name || '');
  iconRoot.setAttribute('group-name', contentGroup?.name || '');
  iconRoot.setAttribute('style', buildCampaignStyle(campaign));
  iconRoot.replaceChildren();

  if (showReadStatus) {
    const border = document.createElement('div');
    border.className = `storeeel-group-border ${allSeen ? 'storeeel-group-seen' : 'storeeel-group-unseen'}`;
    if (thumbnail.playbackVideoUrl) {
      border.dataset.storeeelVideoSrc = thumbnail.playbackVideoUrl;
    }
    border.appendChild(mediaNode);
    appendDeferredVideoNode(border, thumbnail, contentGroup, content, 'storeeel-icon', 'border-radius: 50%; object-fit: cover;');
    iconRoot.appendChild(border);
  } else {
    if (thumbnail.playbackVideoUrl) {
      const mediaWrapper = document.createElement('div');
      mediaWrapper.dataset.storeeelVideoSrc = thumbnail.playbackVideoUrl;
      mediaWrapper.appendChild(mediaNode);
      appendDeferredVideoNode(mediaWrapper, thumbnail, contentGroup, content, 'storeeel-icon', 'border-radius: 50%; object-fit: cover;');
      iconRoot.appendChild(mediaWrapper);
    } else {
      iconRoot.appendChild(mediaNode);
    }
  }

  const groupName = document.createElement('div');
  groupName.className = 'storeeel_campaign_group_name';
  groupName.textContent = contentGroup?.name || '';
  iconRoot.appendChild(groupName);

  if (isAdMediaAsset) {
    const adLabel = document.createElement('div');
    adLabel.className = 'storeeel_campaign_ad_label';
    adLabel.textContent = '【PR】';
    iconRoot.appendChild(adLabel);
  }

  return true;
};

export const refreshPreparedCampaignThumbnailDisplay = (campaign, preparedCampaign, answers = {}) => {
  rememberPreparedCampaignForDisplay(campaign, preparedCampaign, answers);

  const campaignId = String(campaign?.id || '');
  if (!campaignId) return false;
  // リール型・タイル型・ストーリー型のみサムネイル再描画に対応する
  if (!isReelLike(campaign?.frame) && campaign?.frame !== 'circle') return false;

  const root = document.querySelector(`[data-storeeel-campaign-id="${campaignId}"]`);
  const reelBase = root?.querySelector(`.storeeell_base_${campaignId}`);
  if (!reelBase) return false;
  const originalIconContainer = reelBase.querySelector('.storeeel-auto-scroll-track') || reelBase;
  const getOriginalIcons = () => Array.from(originalIconContainer.children)
    .filter((node) => node.classList?.contains?.('storeeel_campaign') && node.dataset?.storeeelAutoScrollClone !== 'true');

  const visibleGroupIds = new Set((preparedCampaign?.contentGroups || []).map((contentGroup) => String(contentGroup?.id)));
  let updated = false;
  let created = false;

  getOriginalIcons().forEach((iconRoot) => {
    const groupId = String(iconRoot.id || '').replace(/^storeeel-icon-/, '');
    if (!groupId || visibleGroupIds.has(groupId)) return;
    iconRoot.remove();
    updated = true;
  });

  (preparedCampaign?.contentGroups || []).forEach((contentGroup) => {
    const iconId = `storeeel-icon-${contentGroup?.id}`;
    const matchedNodes = getOriginalIcons()
      .filter((iconRoot) => iconRoot.id === iconId);

    if (matchedNodes.length === 0) {
      const newIconRoot = document.createElement('div');
      // リール型・タイル型は縦長サムネイルノードで更新する
      const didUpdate = isReelLike(campaign?.frame)
        ? updateSquareThumbnailNode(newIconRoot, campaign, contentGroup)
        : updateCircleThumbnailNode(newIconRoot, campaign, contentGroup);
      if (didUpdate) {
        originalIconContainer.appendChild(newIconRoot);
        updated = true;
        created = true;
      }
      return;
    }

    matchedNodes.forEach((iconRoot) => {
      // リール型・タイル型は縦長サムネイルノードで更新する
      const didUpdate = isReelLike(campaign?.frame)
        ? updateSquareThumbnailNode(iconRoot, campaign, contentGroup)
        : updateCircleThumbnailNode(iconRoot, campaign, contentGroup);
      updated = didUpdate || updated;
    });
  });

  // アンケート回答後に途中のグループが再出現しても、表示順は preparedCampaign の順序に合わせる。
  const orderedIconNodes = (preparedCampaign?.contentGroups || [])
    .map((contentGroup) => getOriginalIcons().find((iconRoot) => iconRoot.id === `storeeel-icon-${contentGroup?.id}`))
    .filter(Boolean);
  const currentIconOrder = getOriginalIcons();
  if (orderedIconNodes.some((iconRoot, index) => currentIconOrder[index] !== iconRoot)) {
    updated = true;
  }
  orderedIconNodes.forEach((iconRoot) => {
    originalIconContainer.appendChild(iconRoot);
  });

  if (updated) {
    reelBase.storeeelRefreshAutoScrollLoop?.();
  }

  return { updated, created };
};
